/**
 * Injectable module registry so core never imports from app (src/config).
 * App calls setModuleRegistryProvider() at startup; threads-client and settings-service use this.
 */

import { ERR_INTERNAL_500 } from '../lib/error-codes';
import { KorvoError } from '../lib/korvo-error';

export type ModuleRegistryProvider = {
  getAvailableModules: () => string[];
  getModuleImport: (moduleName: string) => Promise<unknown>;
  /** Optional hook called after module registry is set up to register package tools. */
  registerModuleTools?: () => Promise<void>;
  /** Optional hook called after all setup is complete. */
  onPostSetup?: () => Promise<void>;
};

let provider: ModuleRegistryProvider | null = null;

export function setModuleRegistryProvider(impl: ModuleRegistryProvider): void {
  provider = impl;
}

export function getModuleRegistryProvider(): ModuleRegistryProvider | null {
  return provider;
}

export function getAvailableModulesFromProvider(): string[] {
  if (!provider) return [];
  try {
    const modules = provider.getAvailableModules();
    return Array.isArray(modules) ? modules : [];
  } catch {
    return [];
  }
}

export async function getModuleImportFromProvider(moduleName: string): Promise<unknown> {
  if (!provider) throw new KorvoError(ERR_INTERNAL_500, 1, { message: 'Module registry provider not set' });
  return provider.getModuleImport(moduleName);
}
